'use client';

import { c as _c } from "react/compiler-runtime";
import type { PopoverContentProps } from '@radix-ui/react-popover';
import { add, eachDayOfInterval, endOfDay, endOfMonth, format, isAfter, isBefore, isSameMonth, parse, startOfDay, startOfToday } from 'date-fns';
import { motion } from 'framer-motion';
import type React from 'react';
import { useMemo, useState } from 'react';
import type { PopoverProps } from '../popover';
import Popover from '../popover/popover';
import DayButton from './components/day-button';
import Header from './components/header';
import RangeInputs from './components/range-inputs';
import WeekHeader from './components/week-header';
import { DirectionChange } from './date-selector-sheet.types';
import { getMoveVariants } from './date-selector-sheet.utils';
const today = startOfToday();
type DateSelectorSheetProps = {
  children: React.ReactNode;
  className?: string;
  disableFutureDates?: boolean;
  disablePastDates?: boolean;
  endDate?: Date;
  onChange: (dates: {
    startDate?: Date;
    endDate?: Date;
  }) => void;
  onPointerDownOutside?: PopoverContentProps['onPointerDownOutside'];
  onEscapeKeyDown?: PopoverContentProps['onEscapeKeyDown'];
  startDate?: Date;
  align?: PopoverProps['align'];
  side?: PopoverProps['side'];
  sideOffset?: PopoverProps['sideOffset'];
  collisionBoundary?: PopoverProps['collisionBoundary'];
  portalContainer?: HTMLElement | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};
const DateSelectorSheet = t0 => {
  const $ = _c(75);
  const {
    children,
    disableFutureDates,
    disablePastDates,
    endDate,
    onChange,
    startDate,
    onPointerDownOutside,
    onEscapeKeyDown,
    align,
    side,
    sideOffset,
    collisionBoundary,
    portalContainer,
    open,
    onOpenChange
  } = t0;
  const [movingDirection, setMovingDirection] = useState();
  let t1;
  if ($[0] !== endDate) {
    t1 = endDate ? format(endDate, "MMM-yyyy") : format(today, "MMM-yyyy");
    $[0] = endDate;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [visibleMonth, setVisibleMonth] = useState(t1);
  let t2;
  if ($[2] !== visibleMonth) {
    t2 = parse(visibleMonth, "MMM-yyyy", new Date());
    $[2] = visibleMonth;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const firstDayCurrentMonth = t2;
  let t3;
  if ($[4] !== firstDayCurrentMonth) {
    t3 = eachDayOfInterval({
      start: firstDayCurrentMonth,
      end: endOfMonth(firstDayCurrentMonth)
    });
    $[4] = firstDayCurrentMonth;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const days = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = targetDate => {
      if (!targetDate) {
        return;
      }
      setVisibleMonth(format(targetDate, "MMM-yyyy"));
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const goToDate = t4;
  let t5;
  if ($[7] !== firstDayCurrentMonth) {
    t5 = direction => {
      const monthAdjustment = direction === DirectionChange.next ? 1 : -1;
      const adjustedMonth = add(firstDayCurrentMonth, {
        months: monthAdjustment
      });
      setVisibleMonth(format(adjustedMonth, "MMM-yyyy"));
    };
    $[7] = firstDayCurrentMonth;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleMonthChange = t5;
  let t6;
  if ($[9] !== endDate || $[10] !== firstDayCurrentMonth || $[11] !== onChange || $[12] !== startDate) {
    t6 = (day, event) => {
      event.stopPropagation();
      event.preventDefault();
      if (!isSameMonth(day, firstDayCurrentMonth)) {
        goToDate(day);
      }
      if (isAfter(day, endDate || day)) {
        onChange({
          startDate: endDate,
          endDate: day
        });
      } else {
        if (isBefore(day, startDate || day)) {
          onChange({
            startDate: day,
            endDate: startDate
          });
        } else {
          onChange({
            startDate: startOfDay(day),
            endDate: endOfDay(day)
          });
        }
      }
    };
    $[9] = endDate;
    $[10] = firstDayCurrentMonth;
    $[11] = onChange;
    $[12] = startDate;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleSelectDay = t6;
  let t7;
  if ($[14] !== endDate || $[15] !== startDate) {
    t7 = trigger => {
      if (trigger === "start") {
        goToDate(startDate || today);
      }
      if (trigger === "end") {
        goToDate(endDate || today);
      }
    };
    $[14] = endDate;
    $[15] = startDate;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleRangeInputFocus = t7;
  let t8;
  if ($[17] !== onChange) {
    t8 = t9 => {
      const {
        startDate: newStartDate,
        endDate: newEndDate,
        trigger: trigger_0
      } = t9;
      onChange({
        startDate: newStartDate,
        endDate: newEndDate
      });
      if (trigger_0 === "start") {
        goToDate(newStartDate);
      }
      if (trigger_0 === "end") {
        goToDate(newEndDate);
      }
    };
    $[17] = onChange;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handleRangeChange = t8;
  let t9;
  if ($[19] !== firstDayCurrentMonth || $[20] !== handleMonthChange || $[21] !== movingDirection) {
    t9 = <Header handleMonthChange={handleMonthChange} firstDayCurrentMonth={firstDayCurrentMonth} movingDirection={movingDirection} setMovingDirection={setMovingDirection} />;
    $[19] = firstDayCurrentMonth;
    $[20] = handleMonthChange;
    $[21] = movingDirection;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== disableFutureDates || $[24] !== disablePastDates || $[25] !== endDate || $[26] !== handleRangeChange || $[27] !== handleRangeInputFocus || $[28] !== startDate) {
    t10 = <RangeInputs onChange={handleRangeChange} onFocus={handleRangeInputFocus} startDate={startDate} endDate={endDate} disableFutureDates={disableFutureDates} disablePastDates={disablePastDates} />;
    $[23] = disableFutureDates;
    $[24] = disablePastDates;
    $[25] = endDate;
    $[26] = handleRangeChange;
    $[27] = handleRangeInputFocus;
    $[28] = startDate;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div id="error-message" />;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <WeekHeader />;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      gridTemplateColumns: "repeat(7, 40px)"
    };
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== firstDayCurrentMonth) {
    t14 = format(firstDayCurrentMonth, "MMMM yyyy");
    $[33] = firstDayCurrentMonth;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const t15 = movingDirection ? "initial" : false;
  const t16 = movingDirection ? "animate" : false;
  let t17;
  if ($[35] !== movingDirection) {
    t17 = movingDirection ? getMoveVariants(movingDirection) : {};
    $[35] = movingDirection;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      duration: 0.5
    };
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== days || $[39] !== disableFutureDates || $[40] !== disablePastDates || $[41] !== endDate || $[42] !== handleSelectDay || $[43] !== startDate || $[44] !== visibleMonth) {
    let t20;
    if ($[46] !== disableFutureDates || $[47] !== disablePastDates || $[48] !== endDate || $[49] !== handleSelectDay || $[50] !== startDate || $[51] !== visibleMonth) {
      t20 = day_0 => <DayButton key={day_0.toISOString()} day={day_0} onClick={event_0 => handleSelectDay(day_0, event_0)} visibleMonth={visibleMonth} activeStartDate={startDate} activeEndDate={endDate} disabled={disableFutureDates && isAfter(day_0, today) || disablePastDates && isBefore(day_0, today)} />;
      $[46] = disableFutureDates;
      $[47] = disablePastDates;
      $[48] = endDate;
      $[49] = handleSelectDay;
      $[50] = startDate;
      $[51] = visibleMonth;
      $[52] = t20;
    } else {
      t20 = $[52];
    }
    t19 = days.map(t20);
    $[38] = days;
    $[39] = disableFutureDates;
    $[40] = disablePastDates;
    $[41] = endDate;
    $[42] = handleSelectDay;
    $[43] = startDate;
    $[44] = visibleMonth;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[53] !== t14 || $[54] !== t15 || $[55] !== t16 || $[56] !== t17 || $[57] !== t19) {
    t20 = <div className="flex flex-col items-center px-2 py-3">{t12}<motion.div className="grid grid-cols-7 gap-0 mx-auto" style={t13} key={t14} initial={t15} animate={t16} exit="exit" variants={t17} transition={t18}>{t19}</motion.div></div>;
    $[53] = t14;
    $[54] = t15;
    $[55] = t16;
    $[56] = t17;
    $[57] = t19;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== t10 || $[60] !== t20 || $[61] !== t9) {
    t21 = <div>{t9}{t10}{t11}{t20}</div>;
    $[59] = t10;
    $[60] = t20;
    $[61] = t9;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  const popoverContent = t21;
  let t22;
  if ($[63] !== align || $[64] !== children || $[65] !== collisionBoundary || $[66] !== onEscapeKeyDown || $[67] !== onOpenChange || $[68] !== onPointerDownOutside || $[69] !== open || $[70] !== popoverContent || $[71] !== portalContainer || $[72] !== side || $[73] !== sideOffset) {
    t22 = <Popover content={popoverContent} onPointerDownOutside={onPointerDownOutside} onEscapeKeyDown={onEscapeKeyDown} side={side} align={align} sideOffset={sideOffset} collisionBoundary={collisionBoundary} portalContainer={portalContainer} controlledOpen={open} onControlledOpenChange={onOpenChange} className="z-toast">{children}</Popover>;
    $[63] = align;
    $[64] = children;
    $[65] = collisionBoundary;
    $[66] = onEscapeKeyDown;
    $[67] = onOpenChange;
    $[68] = onPointerDownOutside;
    $[69] = open;
    $[70] = popoverContent;
    $[71] = portalContainer;
    $[72] = side;
    $[73] = sideOffset;
    $[74] = t22;
  } else {
    t22 = $[74];
  }
  return t22;
};
export default DateSelectorSheet;